import TextButton from 'LuxKit/Button/TextButton'
import React, { useCallback, useContext, useRef } from 'react'
import cn from 'clsx'
import ExperienceSearchCategorySelect from './ExperienceSearchBar/ExperienceSearchCategorySelect'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import { pushWithRegion } from 'actions/NavigationActions'
import { SearchMenuStates } from 'components/Search/type'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { QueryStringSearchParams, replaceExperienceQueryString } from 'lib/experiences/experienceUtils'

import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import GlobalSearchTrackingContext from 'contexts/GlobalSearch/GlobalSearchTracking'
import { OFFER_TYPE_EXPERIENCE } from 'constants/offer'
import { EXPERIENCE_LOCATION_SEARCH_PLACEHOLDER_LABEL, EXPERIENCE_LOCATION_SEARCH_TITLE_LABEL, EXPERIENCE_TYPEAHEAD_SEARCH_TYPES } from 'constants/experience'
import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import * as Analytics from 'analytics/analytics'
import { searchEventWithContext } from 'analytics/snowplow/events'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import IconButton from 'LuxKit/Button/IconButton'

const SearchContainer = styled(CSSBreakpoint)`
  display: grid;
  gap: ${rem(12)};
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) ${rem(112)};

  &.map-view {
    grid-template-columns: 1fr auto;
    width: 100%;
  }
`

const SearchButton = styled(TextButton)`
  height: 100%;
`

const StyledForm = styled.form`
  &.map-view {
    flex: 1;
    width: 100%;
  }
`

interface Props {
  className?: string
  onSearch?: () => void
  isMapView?: boolean
}

function ExperienceSearchForm(props: Props) {
  const { className, onSearch, isMapView } = props
  const formRef = useRef<HTMLFormElement>(null)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const onSearchEvent = useContext(GlobalSearchTrackingContext)
  const dispatch = useAppDispatch()

  const {
    searchItem,
    experienceCategories,
    activeMenu,
  } = useContext(GlobalSearchStateContext)
  const toBeSearchedItem = searchItem ?? ANYWHERE_SEARCH_ITEM
  const fetchingExperience = useAppSelector(state => state.experience.fetchingExperiences[toBeSearchedItem.value])

  const onSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (activeMenu === SearchMenuStates.ExperienceCategories && !searchItem) {
      searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Locations })
      return
    } else if (activeMenu === SearchMenuStates.Locations && !experienceCategories?.length) {
      searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.ExperienceCategories })
      return
    } else {
      searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: SearchMenuStates.Closed })
    }

    const query: QueryStringSearchParams = {
      categories: experienceCategories,
    }

    if (toBeSearchedItem.searchType === 'experience') {
      query.experienceId = toBeSearchedItem.value
      query.experienceName = toBeSearchedItem.format.mainText
    } else {
      query.destinationId = toBeSearchedItem.value
      query.destinationName = toBeSearchedItem.format.mainText
    }

    const queryString = replaceExperienceQueryString(query)

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        searchItem,
        searchVerticals: new Set(['experiences']),
      },
      ),
    }))

    const path = isMapView ? '/search/experiences/map' : '/search/experiences'

    dispatch(pushWithRegion(`${path}?${queryString}`))

    onSearchEvent('submit-button', {
      searchItem,
      vertical: OFFER_TYPE_EXPERIENCE,
    })
    onSearch?.()
  }

  const onChangeLocation = useCallback((searchItem: App.SearchItem) => {
    if (!isMapView) return
    const query: QueryStringSearchParams = {
      categories: experienceCategories,
      destinationId: searchItem.value,
      destinationName: searchItem.format.mainText,
    }

    const queryString = replaceExperienceQueryString(query)

    Analytics.trackEvent(searchEventWithContext({
      searchEvent: { type: 'search_bar' },
      sortContext: { value: 'recommended' },
      searchContext: mapGlobalSearchContextToSnowplowSearchEvent({
        searchItem,
        searchVerticals: new Set(['experiences']),
      },
      ),
    }))

    dispatch(pushWithRegion(`/search/experiences/map?${queryString}`))

    onSearchEvent('submit-button', {
      searchItem,
      vertical: OFFER_TYPE_EXPERIENCE,
    })
  }, [isMapView, dispatch, experienceCategories, onSearchEvent])

  return (
    <StyledForm className={cn(className, { 'map-view': isMapView })} onSubmit={onSearchSubmit} ref={formRef}>
      <SearchContainer min="tablet" className={cn({ 'map-view': isMapView })}>
        <SearchLocationInput
          placeTypes={EXPERIENCE_TYPEAHEAD_SEARCH_TYPES}
          label={EXPERIENCE_LOCATION_SEARCH_TITLE_LABEL}
          placeholder={EXPERIENCE_LOCATION_SEARCH_PLACEHOLDER_LABEL}
          searchVertical="experience"
          variant="dropdown"
          onChange={onChangeLocation}
        />
        {!isMapView && <ExperienceSearchCategorySelect />}
        {!isMapView && <SearchButton kind="primary" type="submit" size="large" disabled={fetchingExperience}>
          Search
        </SearchButton>}
        {isMapView && <IconButton
          size="large"
          kind="primary"
          shape="square"
          type="submit"
          disabled={fetchingExperience}
        >
          <LineSearchIcon />
        </IconButton>}
      </SearchContainer>
    </StyledForm>
  )
}

export default ExperienceSearchForm
